import { z } from "zod";
import { customerOperationsEventIdSchema, customerOperationsOpaqueIdSchema, } from "./customer-operations-core.js";
import { supportIdempotencyDecisionSchema, supportTicketReceiptSchema, } from "./customer-operations-support.js";
z.config({ jitless: true });
export const SUPPORT_TICKETS_PER_15_MINUTES = 3;
export const SUPPORT_TICKETS_PER_24_HOURS = 10;
export const SUPPORT_MAX_UNRESOLVED_TICKETS = 5;
export const SUPPORT_RETRY_AFTER_MAX_SECONDS = 24 * 60 * 60;
export const supportIntakeAtomicityPolicy = Object.freeze({
    idempotencyDecisionBeforeQuota: true,
    receiptAndCountersInOneTransaction: true,
    replayConsumesQuota: false,
    rejectionCreatesReceipt: false,
});
export const supportIntakeQuotaSnapshotSchema = z
    .object({
    acceptedTicketTimes: z.array(z.string().datetime()).max(1_000),
    unresolvedTicketCount: z.number().int().nonnegative().max(1_000_000),
})
    .strict();
export const supportIntakePolicyDecisionSchema = z.discriminatedUnion("decision", [
    z
        .object({
        decision: z.literal("ACCEPT"),
        consumesQuota: z.literal(true),
        increments: z
            .object({
            tickets15Minutes: z.literal(1),
            tickets24Hours: z.literal(1),
            unresolvedTickets: z.literal(1),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        decision: z.literal("REPLAY"),
        consumesQuota: z.literal(false),
        receipt: supportTicketReceiptSchema,
    })
        .strict(),
    z
        .object({
        decision: z.literal("CONFLICT"),
        consumesQuota: z.literal(false),
        reasonCode: z.literal("IDEMPOTENCY_KEY_REUSED"),
    })
        .strict(),
    z
        .object({
        decision: z.literal("RATE_LIMITED"),
        consumesQuota: z.literal(false),
        reasonCode: z.literal("RATE_LIMITED"),
        retryAfterSeconds: z.number().int().min(1).max(SUPPORT_RETRY_AFTER_MAX_SECONDS),
    })
        .strict(),
]);
function entriesInsideWindow(times, nowMs, windowMs) {
    const boundary = nowMs - windowMs;
    return times.map(Date.parse).filter((timestamp) => timestamp > boundary && timestamp <= nowMs);
}
function secondsUntilWindowAllows(timestamps, windowMs, nowMs) {
    if (!timestamps.length)
        return 1;
    const earliest = Math.min(...timestamps);
    return Math.max(1, Math.ceil((earliest + windowMs - nowMs) / 1_000));
}
export function evaluateSupportIntakePolicy(input) {
    const idempotency = supportIdempotencyDecisionSchema.parse(input.idempotency);
    if (idempotency.decision === "REPLAY")
        return supportIntakePolicyDecisionSchema.parse({
            decision: "REPLAY",
            consumesQuota: false,
            receipt: idempotency.receipt,
        });
    if (idempotency.decision === "CONFLICT")
        return {
            decision: "CONFLICT",
            consumesQuota: false,
            reasonCode: "IDEMPOTENCY_KEY_REUSED",
        };
    const now = z.string().datetime().parse(input.now);
    const nowMs = Date.parse(now);
    const quota = supportIntakeQuotaSnapshotSchema.parse(input.quota);
    const recent15Minutes = entriesInsideWindow(quota.acceptedTicketTimes, nowMs, 15 * 60_000);
    const recent24Hours = entriesInsideWindow(quota.acceptedTicketTimes, nowMs, 24 * 60 * 60_000);
    const retryCandidates = [];
    if (recent15Minutes.length >= SUPPORT_TICKETS_PER_15_MINUTES)
        retryCandidates.push(secondsUntilWindowAllows(recent15Minutes, 15 * 60_000, nowMs));
    if (recent24Hours.length >= SUPPORT_TICKETS_PER_24_HOURS)
        retryCandidates.push(secondsUntilWindowAllows(recent24Hours, 24 * 60 * 60_000, nowMs));
    if (quota.unresolvedTicketCount >= SUPPORT_MAX_UNRESOLVED_TICKETS)
        retryCandidates.push(15 * 60);
    if (retryCandidates.length)
        return supportIntakePolicyDecisionSchema.parse({
            decision: "RATE_LIMITED",
            consumesQuota: false,
            reasonCode: "RATE_LIMITED",
            retryAfterSeconds: Math.min(SUPPORT_RETRY_AFTER_MAX_SECONDS, Math.max(...retryCandidates)),
        });
    return {
        decision: "ACCEPT",
        consumesQuota: true,
        increments: { tickets15Minutes: 1, tickets24Hours: 1, unresolvedTickets: 1 },
    };
}
export const customerOperationsRetentionClassSchema = z.enum([
    "EVENTBRIDGE_REPLAY_ARCHIVE",
    "SANITIZED_OPERATIONS_PROJECTION",
    "SUPPORT_BODY",
    "SUPPORT_CASE_METADATA",
    "PUBLISHED_OUTBOX_EVIDENCE",
    "PENDING_OR_UNRECONCILED_OUTBOX",
    "IMMUTABLE_AUDIT_EVIDENCE",
]);
export const customerOperationsRetentionDays = Object.freeze({
    EVENTBRIDGE_REPLAY_ARCHIVE: 30,
    SANITIZED_OPERATIONS_PROJECTION: 730,
    SUPPORT_BODY_AFTER_RESOLUTION: 90,
    SUPPORT_BODY_AFTER_RECEIPT: 365,
    SUPPORT_CASE_METADATA_AFTER_CLOSURE: 730,
    PUBLISHED_OUTBOX_EVIDENCE_AFTER_PUBLICATION: 30,
    IMMUTABLE_AUDIT_EVIDENCE: 2_555,
});
export const customerOperationsRetentionPurposePolicy = Object.freeze({
    EVENTBRIDGE_REPLAY_ARCHIVE: {
        immutableAuditArchive: false,
        purpose: "BOUNDED_OPERATIONAL_REPLAY",
    },
    IMMUTABLE_AUDIT_EVIDENCE: {
        immutableAuditArchive: true,
        purpose: "IMMUTABLE_AUDIT_EVIDENCE",
    },
});
export const customerOperationsDeletionStateSchema = z.enum([
    "RETAIN",
    "DELETION_DUE",
    "LEGAL_HOLD",
    "DELETING",
    "DELETED",
    "NEEDS_ATTENTION",
]);
export const customerOperationsRetentionHoldSchema = z
    .object({
    holdId: customerOperationsOpaqueIdSchema,
    placedAuditEventId: customerOperationsEventIdSchema,
    state: z.enum(["ACTIVE", "RELEASED"]),
    placedAt: z.string().datetime(),
    releaseAuditEventId: customerOperationsEventIdSchema.optional(),
    releasedAt: z.string().datetime().optional(),
})
    .strict()
    .superRefine((hold, context) => {
    if (hold.state === "ACTIVE" && (hold.releasedAt || hold.releaseAuditEventId))
        context.addIssue({
            code: "custom",
            path: ["releasedAt"],
            message: "An active retention hold cannot have release evidence.",
        });
    if (hold.state === "RELEASED" && (!hold.releasedAt || !hold.releaseAuditEventId))
        context.addIssue({
            code: "custom",
            path: ["releasedAt"],
            message: "A released retention hold requires time and opaque audit evidence.",
        });
    if (hold.state === "RELEASED" && hold.releaseAuditEventId === hold.placedAuditEventId)
        context.addIssue({
            code: "custom",
            path: ["releaseAuditEventId"],
            message: "Hold release requires audit evidence distinct from hold placement.",
        });
    if (hold.releasedAt && Date.parse(hold.releasedAt) < Date.parse(hold.placedAt))
        context.addIssue({
            code: "custom",
            path: ["releasedAt"],
            message: "Hold release cannot precede placement.",
        });
});
export const customerOperationsRetentionInputSchema = z
    .object({
    retentionClass: customerOperationsRetentionClassSchema,
    currentState: customerOperationsDeletionStateSchema,
    now: z.string().datetime(),
    createdAt: z.string().datetime(),
    resolvedAt: z.string().datetime().optional(),
    closedAt: z.string().datetime().optional(),
    publishedAt: z.string().datetime().optional(),
    publicationReconciled: z.boolean().optional(),
    customerDeletionRequestedAt: z.string().datetime().optional(),
    deletionStartedAt: z.string().datetime().optional(),
    deletedAt: z.string().datetime().optional(),
    hold: customerOperationsRetentionHoldSchema.optional(),
})
    .strict()
    .superRefine((input, context) => {
    const notBeforeCreated = [
        "now",
        "resolvedAt",
        "closedAt",
        "publishedAt",
        "customerDeletionRequestedAt",
        "deletionStartedAt",
        "deletedAt",
    ];
    for (const field of notBeforeCreated) {
        const value = input[field];
        if (value && Date.parse(value) < Date.parse(input.createdAt))
            context.addIssue({
                code: "custom",
                path: [field],
                message: `${field} cannot precede creation.`,
            });
    }
    for (const field of [
        "resolvedAt",
        "closedAt",
        "publishedAt",
        "customerDeletionRequestedAt",
    ]) {
        const value = input[field];
        if (value && Date.parse(value) > Date.parse(input.now))
            context.addIssue({
                code: "custom",
                path: [field],
                message: `${field} cannot be in the future.`,
            });
    }
    if (input.resolvedAt &&
        input.closedAt &&
        Date.parse(input.closedAt) < Date.parse(input.resolvedAt))
        context.addIssue({
            code: "custom",
            path: ["closedAt"],
            message: "Closure cannot precede resolution.",
        });
    if (input.deletionStartedAt &&
        input.deletedAt &&
        Date.parse(input.deletedAt) < Date.parse(input.deletionStartedAt))
        context.addIssue({
            code: "custom",
            path: ["deletedAt"],
            message: "Deletion completion cannot precede deletion start.",
        });
    if (input.currentState === "DELETING") {
        if (!input.deletionStartedAt)
            context.addIssue({
                code: "custom",
                path: ["deletionStartedAt"],
                message: "Deleting state requires a deletion start time.",
            });
        if (input.deletedAt)
            context.addIssue({
                code: "custom",
                path: ["deletedAt"],
                message: "Deleting state cannot contain completion evidence.",
            });
    }
    else if (input.currentState === "DELETED") {
        if (!input.deletionStartedAt || !input.deletedAt)
            context.addIssue({
                code: "custom",
                path: ["deletedAt"],
                message: "Deleted state requires start and completion times.",
            });
    }
    else if (input.deletionStartedAt || input.deletedAt) {
        context.addIssue({
            code: "custom",
            path: [input.deletedAt ? "deletedAt" : "deletionStartedAt"],
            message: "Deletion timestamps are incompatible with the current state.",
        });
    }
    for (const field of ["deletionStartedAt", "deletedAt"]) {
        const value = input[field];
        if (value && Date.parse(value) > Date.parse(input.now))
            context.addIssue({
                code: "custom",
                path: [field],
                message: `${field} cannot be in the future.`,
            });
    }
    if (input.hold && Date.parse(input.hold.placedAt) < Date.parse(input.createdAt))
        context.addIssue({
            code: "custom",
            path: ["hold", "placedAt"],
            message: "A hold cannot precede the retained record.",
        });
    if (input.currentState === "LEGAL_HOLD" && input.hold?.state !== "ACTIVE")
        context.addIssue({
            code: "custom",
            path: ["hold"],
            message: "Legal-hold state requires exact active hold evidence.",
        });
    if (input.hold && Date.parse(input.hold.placedAt) > Date.parse(input.now))
        context.addIssue({
            code: "custom",
            path: ["hold", "placedAt"],
            message: "Hold placement cannot be in the future.",
        });
    if (input.hold?.state === "RELEASED" &&
        input.hold.releasedAt &&
        Date.parse(input.hold.releasedAt) > Date.parse(input.now))
        context.addIssue({
            code: "custom",
            path: ["hold", "releasedAt"],
            message: "A future release is not completed release evidence.",
        });
});
export const customerOperationsDeletionEligibilitySchema = z
    .object({
    state: customerOperationsDeletionStateSchema,
    deletionDueAt: z.string().datetime().optional(),
})
    .strict();
function addDays(value, days) {
    return new Date(Date.parse(value) + days * 24 * 60 * 60_000).toISOString();
}
function earlier(...values) {
    const present = values.filter((value) => value !== undefined);
    return present.sort((left, right) => left.localeCompare(right))[0];
}
function retentionDeadline(input) {
    switch (input.retentionClass) {
        case "EVENTBRIDGE_REPLAY_ARCHIVE":
            return addDays(input.createdAt, customerOperationsRetentionDays.EVENTBRIDGE_REPLAY_ARCHIVE);
        case "SANITIZED_OPERATIONS_PROJECTION":
            return addDays(input.createdAt, customerOperationsRetentionDays.SANITIZED_OPERATIONS_PROJECTION);
        case "SUPPORT_BODY":
            return earlier(addDays(input.createdAt, customerOperationsRetentionDays.SUPPORT_BODY_AFTER_RECEIPT), input.resolvedAt
                ? addDays(input.resolvedAt, customerOperationsRetentionDays.SUPPORT_BODY_AFTER_RESOLUTION)
                : undefined, input.customerDeletionRequestedAt);
        case "SUPPORT_CASE_METADATA":
            return input.closedAt
                ? addDays(input.closedAt, customerOperationsRetentionDays.SUPPORT_CASE_METADATA_AFTER_CLOSURE)
                : undefined;
        case "PUBLISHED_OUTBOX_EVIDENCE":
            return input.publishedAt && input.publicationReconciled === true
                ? addDays(input.publishedAt, customerOperationsRetentionDays.PUBLISHED_OUTBOX_EVIDENCE_AFTER_PUBLICATION)
                : undefined;
        case "PENDING_OR_UNRECONCILED_OUTBOX":
            return undefined;
        case "IMMUTABLE_AUDIT_EVIDENCE":
            return addDays(input.createdAt, customerOperationsRetentionDays.IMMUTABLE_AUDIT_EVIDENCE);
    }
}
export function evaluateCustomerOperationsDeletionEligibility(inputValue) {
    const input = customerOperationsRetentionInputSchema.parse(inputValue);
    if (input.currentState === "DELETED")
        return { state: "DELETED" };
    if (input.hold?.state === "ACTIVE")
        return { state: "LEGAL_HOLD" };
    if (input.retentionClass === "PENDING_OR_UNRECONCILED_OUTBOX")
        return { state: "RETAIN" };
    if (input.retentionClass === "PUBLISHED_OUTBOX_EVIDENCE" &&
        (!input.publishedAt || input.publicationReconciled !== true))
        return { state: "NEEDS_ATTENTION" };
    if (input.currentState === "DELETING")
        return { state: "DELETING" };
    const deletionDueAt = retentionDeadline(input);
    if (!deletionDueAt)
        return { state: "RETAIN" };
    return customerOperationsDeletionEligibilitySchema.parse({
        state: Date.parse(input.now) >= Date.parse(deletionDueAt) ? "DELETION_DUE" : "RETAIN",
        deletionDueAt,
    });
}
const allowedDeletionTransitions = {
    RETAIN: ["RETAIN", "DELETION_DUE", "LEGAL_HOLD", "NEEDS_ATTENTION"],
    DELETION_DUE: ["DELETION_DUE", "LEGAL_HOLD", "DELETING", "NEEDS_ATTENTION"],
    LEGAL_HOLD: ["LEGAL_HOLD", "RETAIN", "DELETION_DUE", "NEEDS_ATTENTION"],
    DELETING: ["DELETING", "DELETED", "LEGAL_HOLD", "NEEDS_ATTENTION"],
    DELETED: ["DELETED"],
    NEEDS_ATTENTION: ["NEEDS_ATTENTION", "RETAIN", "DELETION_DUE", "LEGAL_HOLD"],
};
export const legalHoldDeletionAtomicityPolicy = Object.freeze({
    purePolicyProvidesTransaction: false,
    serializeHoldActivationWithDeletionClaim: true,
    activeHoldWinsBeforePhysicalDeletion: true,
    runtimeAtomicBoundary: "HOLD_CHECK_AND_DELETION_CLAIM",
    physicalDeletionIsPointOfNoReturn: true,
});
const activeHoldSchema = customerOperationsRetentionHoldSchema.refine((hold) => hold.state === "ACTIVE", { message: "Active hold evidence is required." });
const releasedHoldSchema = customerOperationsRetentionHoldSchema.refine((hold) => hold.state === "RELEASED", { message: "Released hold evidence is required." });
export const customerOperationsDeletionTransitionEvidenceSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("NONE") }).strict(),
    z.object({ kind: z.literal("ACTIVE_HOLD"), hold: activeHoldSchema }).strict(),
    z.object({ kind: z.literal("RELEASED_HOLD"), hold: releasedHoldSchema }).strict(),
    z
        .object({
        kind: z.literal("HOLD_REPLACED"),
        releasedHold: releasedHoldSchema,
        activeHold: activeHoldSchema,
    })
        .strict()
        .superRefine((evidence, context) => {
        if (evidence.releasedHold.holdId === evidence.activeHold.holdId)
            context.addIssue({
                code: "custom",
                path: ["activeHold", "holdId"],
                message: "A replacement hold requires a new hold identifier.",
            });
        if (evidence.releasedHold.releasedAt &&
            Date.parse(evidence.activeHold.placedAt) < Date.parse(evidence.releasedHold.releasedAt))
            context.addIssue({
                code: "custom",
                path: ["activeHold", "placedAt"],
                message: "Replacement placement cannot precede release of the prior hold.",
            });
    }),
    z
        .object({
        kind: z.literal("DELETION_STARTED"),
        deletionStartedAt: z.string().datetime(),
    })
        .strict(),
    z
        .object({
        kind: z.literal("DELETION_COMPLETED"),
        deletionStartedAt: z.string().datetime(),
        deletedAt: z.string().datetime(),
    })
        .strict()
        .refine((evidence) => Date.parse(evidence.deletedAt) >= Date.parse(evidence.deletionStartedAt), { path: ["deletedAt"], message: "Deletion completion cannot precede start." }),
]);
export const customerOperationsDeletionTransitionCurrentSchema = z
    .object({
    state: customerOperationsDeletionStateSchema,
    now: z.string().datetime(),
    createdAt: z.string().datetime(),
    activeHold: activeHoldSchema.optional(),
    deletionStartedAt: z.string().datetime().optional(),
    deletedAt: z.string().datetime().optional(),
})
    .strict()
    .superRefine((current, context) => {
    if (Date.parse(current.now) < Date.parse(current.createdAt))
        context.addIssue({
            code: "custom",
            path: ["now"],
            message: "Transition time cannot precede record creation.",
        });
    if (current.state === "LEGAL_HOLD" && !current.activeHold)
        context.addIssue({
            code: "custom",
            path: ["activeHold"],
            message: "Legal-hold state requires exact active hold evidence.",
        });
    if (current.state !== "LEGAL_HOLD" && current.activeHold)
        context.addIssue({
            code: "custom",
            path: ["activeHold"],
            message: "Only legal-hold state may carry active hold evidence.",
        });
    if (current.activeHold && Date.parse(current.activeHold.placedAt) > Date.parse(current.now))
        context.addIssue({
            code: "custom",
            path: ["activeHold", "placedAt"],
            message: "Current active hold placement cannot be in the future.",
        });
    if (current.activeHold &&
        Date.parse(current.activeHold.placedAt) < Date.parse(current.createdAt))
        context.addIssue({
            code: "custom",
            path: ["activeHold", "placedAt"],
            message: "Current active hold placement cannot precede record creation.",
        });
    if (current.state === "DELETING") {
        if (!current.deletionStartedAt || current.deletedAt)
            context.addIssue({
                code: "custom",
                path: ["deletionStartedAt"],
                message: "Deleting transition state requires only its claimed start.",
            });
    }
    else if (current.state === "DELETED") {
        if (!current.deletionStartedAt || !current.deletedAt)
            context.addIssue({
                code: "custom",
                path: ["deletedAt"],
                message: "Deleted transition state requires start and completion evidence.",
            });
    }
    else if (current.deletionStartedAt || current.deletedAt) {
        context.addIssue({
            code: "custom",
            path: [current.deletedAt ? "deletedAt" : "deletionStartedAt"],
            message: "Deletion timestamps do not belong to this transition state.",
        });
    }
    if (current.deletionStartedAt &&
        Date.parse(current.deletionStartedAt) < Date.parse(current.createdAt))
        context.addIssue({
            code: "custom",
            path: ["deletionStartedAt"],
            message: "Claimed deletion start cannot precede record creation.",
        });
    if (current.deletedAt && Date.parse(current.deletedAt) < Date.parse(current.createdAt))
        context.addIssue({
            code: "custom",
            path: ["deletedAt"],
            message: "Deletion completion cannot precede record creation.",
        });
    if (current.deletionStartedAt &&
        Date.parse(current.deletionStartedAt) > Date.parse(current.now))
        context.addIssue({
            code: "custom",
            path: ["deletionStartedAt"],
            message: "Claimed deletion start cannot be in the future.",
        });
    if (current.deletedAt && Date.parse(current.deletedAt) > Date.parse(current.now))
        context.addIssue({
            code: "custom",
            path: ["deletedAt"],
            message: "Deletion completion cannot be in the future.",
        });
    if (current.deletionStartedAt &&
        current.deletedAt &&
        Date.parse(current.deletedAt) < Date.parse(current.deletionStartedAt))
        context.addIssue({
            code: "custom",
            path: ["deletedAt"],
            message: "Deletion completion cannot precede the claimed start.",
        });
});
function sameHoldPlacement(active, other) {
    return (active.holdId === other.holdId &&
        active.placedAuditEventId === other.placedAuditEventId &&
        active.placedAt === other.placedAt);
}
export function transitionCustomerOperationsDeletionState(currentValue, nextValue, evidenceValue = { kind: "NONE" }) {
    const current = customerOperationsDeletionTransitionCurrentSchema.parse(currentValue);
    const next = customerOperationsDeletionStateSchema.parse(nextValue);
    const evidence = customerOperationsDeletionTransitionEvidenceSchema.parse(evidenceValue);
    if (!allowedDeletionTransitions[current.state].includes(next))
        throw new Error("CUSTOMER_OPERATIONS_DELETION_TRANSITION_INVALID");
    if (evidence.kind === "ACTIVE_HOLD" &&
        Date.parse(evidence.hold.placedAt) > Date.parse(current.now))
        throw new Error("CUSTOMER_OPERATIONS_HOLD_PLACEMENT_IN_FUTURE");
    if (evidence.kind === "ACTIVE_HOLD" &&
        Date.parse(evidence.hold.placedAt) < Date.parse(current.createdAt))
        throw new Error("CUSTOMER_OPERATIONS_HOLD_PLACEMENT_BEFORE_RECORD_CREATION");
    if (evidence.kind === "RELEASED_HOLD" &&
        evidence.hold.releasedAt &&
        Date.parse(evidence.hold.releasedAt) > Date.parse(current.now))
        throw new Error("CUSTOMER_OPERATIONS_HOLD_RELEASE_IN_FUTURE");
    if (evidence.kind === "HOLD_REPLACED") {
        if (!evidence.releasedHold.releasedAt ||
            Date.parse(evidence.releasedHold.releasedAt) > Date.parse(current.now) ||
            Date.parse(evidence.activeHold.placedAt) > Date.parse(current.now))
            throw new Error("CUSTOMER_OPERATIONS_HOLD_REPLACEMENT_NOT_EFFECTIVE");
        if (Date.parse(evidence.releasedHold.placedAt) < Date.parse(current.createdAt) ||
            Date.parse(evidence.activeHold.placedAt) < Date.parse(current.createdAt))
            throw new Error("CUSTOMER_OPERATIONS_HOLD_PLACEMENT_BEFORE_RECORD_CREATION");
    }
    if (evidence.kind === "DELETION_STARTED" &&
        Date.parse(evidence.deletionStartedAt) > Date.parse(current.now))
        throw new Error("CUSTOMER_OPERATIONS_DELETION_START_IN_FUTURE");
    if ((evidence.kind === "DELETION_STARTED" || evidence.kind === "DELETION_COMPLETED") &&
        Date.parse(evidence.deletionStartedAt) < Date.parse(current.createdAt))
        throw new Error("CUSTOMER_OPERATIONS_DELETION_START_BEFORE_RECORD_CREATION");
    if (evidence.kind === "DELETION_COMPLETED") {
        if (Date.parse(evidence.deletedAt) > Date.parse(current.now))
            throw new Error("CUSTOMER_OPERATIONS_DELETION_COMPLETION_IN_FUTURE");
        if (current.state === "DELETING" && evidence.deletionStartedAt !== current.deletionStartedAt)
            throw new Error("CUSTOMER_OPERATIONS_DELETION_START_MISMATCH");
        if (current.state === "DELETED" &&
            (evidence.deletionStartedAt !== current.deletionStartedAt ||
                evidence.deletedAt !== current.deletedAt))
            throw new Error("CUSTOMER_OPERATIONS_DELETION_COMPLETION_MISMATCH");
    }
    if (current.state === "DELETING" &&
        evidence.kind === "DELETION_STARTED" &&
        evidence.deletionStartedAt !== current.deletionStartedAt)
        throw new Error("CUSTOMER_OPERATIONS_DELETION_START_MISMATCH");
    if (next === "LEGAL_HOLD") {
        if (current.state !== "LEGAL_HOLD" && evidence.kind !== "ACTIVE_HOLD")
            throw new Error("CUSTOMER_OPERATIONS_ACTIVE_HOLD_EVIDENCE_REQUIRED");
        if (current.state === "LEGAL_HOLD") {
            if (!current.activeHold)
                throw new Error("CUSTOMER_OPERATIONS_ACTIVE_HOLD_EVIDENCE_REQUIRED");
            if (evidence.kind === "ACTIVE_HOLD" && !sameHoldPlacement(current.activeHold, evidence.hold))
                throw new Error("CUSTOMER_OPERATIONS_HOLD_REPLACEMENT_EVIDENCE_REQUIRED");
            if (evidence.kind === "HOLD_REPLACED") {
                if (!sameHoldPlacement(current.activeHold, evidence.releasedHold))
                    throw new Error("CUSTOMER_OPERATIONS_RELEASED_HOLD_ID_MISMATCH");
            }
            else if (evidence.kind !== "ACTIVE_HOLD") {
                throw new Error("CUSTOMER_OPERATIONS_HOLD_REPLACEMENT_EVIDENCE_REQUIRED");
            }
        }
    }
    if (current.state === "LEGAL_HOLD" && next !== "LEGAL_HOLD") {
        if (evidence.kind !== "RELEASED_HOLD")
            throw new Error("CUSTOMER_OPERATIONS_RELEASED_HOLD_EVIDENCE_REQUIRED");
        if (!current.activeHold || !sameHoldPlacement(current.activeHold, evidence.hold))
            throw new Error("CUSTOMER_OPERATIONS_RELEASED_HOLD_ID_MISMATCH");
    }
    if (next === "DELETING" && evidence.kind !== "DELETION_STARTED")
        throw new Error("CUSTOMER_OPERATIONS_DELETION_START_EVIDENCE_REQUIRED");
    if (next === "DELETED" && evidence.kind !== "DELETION_COMPLETED")
        throw new Error("CUSTOMER_OPERATIONS_DELETION_COMPLETION_EVIDENCE_REQUIRED");
    return next;
}
export const supportEmailPolicySchema = z
    .object({
    canonicalChannel: z.literal("AUTHENTICATED_IN_APP"),
    emailFallback: z.literal("NON_CANONICAL"),
    emailMayClaimTicketReceipt: z.literal(false),
})
    .strict();
export const supportEmailPolicy = supportEmailPolicySchema.parse({
    canonicalChannel: "AUTHENTICATED_IN_APP",
    emailFallback: "NON_CANONICAL",
    emailMayClaimTicketReceipt: false,
});
export const futureSupportPermissionSchema = z.enum([
    "SUPPORT_INTAKE_LIST",
    "SUPPORT_INTAKE_CLAIM",
    "SUPPORT_INTAKE_ASSIGN",
    "SUPPORT_TICKET_BODY_READ",
    "SUPPORT_TICKET_RESPOND",
    "SUPPORT_TICKET_RESOLVE",
    "SUPPORT_RETENTION_HOLD_MANAGE",
]);
export const futureSupportRoleSchema = z.enum(["ADMIN", "SUPPORT_AGENT", "BUSINESS_PARTNER"]);
export const supportCaseAccessStateSchema = z.enum(["UNASSIGNED_OPEN", "ASSIGNED_OPEN", "CLOSED"]);
export const supportAssignmentRelationSchema = z.enum(["UNASSIGNED", "SELF", "OTHER"]);
export const supportPermissionContextSchema = z
    .object({
    role: futureSupportRoleSchema,
    permission: futureSupportPermissionSchema,
    caseState: supportCaseAccessStateSchema,
    assignment: supportAssignmentRelationSchema,
    auditRecorded: z.boolean(),
})
    .strict();
export const supportPermissionDecisionSchema = z
    .object({
    allowed: z.boolean(),
    auditRequired: z.boolean(),
    bodyAccess: z.enum(["NONE", "PRIVATE_BODY"]),
    reasonCode: z.enum([
        "ALLOWED",
        "ROLE_DENIED",
        "CASE_NOT_OPEN",
        "CASE_NOT_ASSIGNED_TO_OPERATOR",
        "AUDIT_REQUIRED",
    ]),
})
    .strict();
const supportMutationPermissions = new Set([
    "SUPPORT_INTAKE_CLAIM",
    "SUPPORT_INTAKE_ASSIGN",
    "SUPPORT_TICKET_BODY_READ",
    "SUPPORT_TICKET_RESPOND",
    "SUPPORT_TICKET_RESOLVE",
    "SUPPORT_RETENTION_HOLD_MANAGE",
]);
export function authorizeFutureSupportPermission(inputValue) {
    const input = supportPermissionContextSchema.parse(inputValue);
    const auditRequired = supportMutationPermissions.has(input.permission);
    const denied = (reasonCode) => ({
        allowed: false,
        auditRequired,
        bodyAccess: "NONE",
        reasonCode,
    });
    if (input.role === "BUSINESS_PARTNER")
        return denied("ROLE_DENIED");
    if (input.permission === "SUPPORT_INTAKE_LIST")
        return { allowed: true, auditRequired: false, bodyAccess: "NONE", reasonCode: "ALLOWED" };
    if (auditRequired && !input.auditRecorded)
        return denied("AUDIT_REQUIRED");
    if (input.permission === "SUPPORT_RETENTION_HOLD_MANAGE")
        return input.role === "ADMIN"
            ? { allowed: true, auditRequired: true, bodyAccess: "NONE", reasonCode: "ALLOWED" }
            : denied("ROLE_DENIED");
    if (input.permission === "SUPPORT_INTAKE_ASSIGN") {
        if (input.role !== "ADMIN")
            return denied("ROLE_DENIED");
        if (input.caseState === "CLOSED")
            return denied("CASE_NOT_OPEN");
        return { allowed: true, auditRequired: true, bodyAccess: "NONE", reasonCode: "ALLOWED" };
    }
    if (input.permission === "SUPPORT_INTAKE_CLAIM") {
        if (input.caseState !== "UNASSIGNED_OPEN" || input.assignment !== "UNASSIGNED")
            return denied("CASE_NOT_OPEN");
        return { allowed: true, auditRequired: true, bodyAccess: "NONE", reasonCode: "ALLOWED" };
    }
    if (input.caseState !== "ASSIGNED_OPEN")
        return denied("CASE_NOT_OPEN");
    if (input.assignment !== "SELF")
        return denied("CASE_NOT_ASSIGNED_TO_OPERATOR");
    return {
        allowed: true,
        auditRequired: true,
        bodyAccess: input.permission === "SUPPORT_TICKET_BODY_READ" ? "PRIVATE_BODY" : "NONE",
        reasonCode: "ALLOWED",
    };
}
const cognitoTriggerTelemetrySchema = z
    .object({
    failureMode: z.literal("FAIL_OPEN"),
    evidenceClass: z.literal("BEST_EFFORT_TELEMETRY"),
    atomicAuthenticationTruth: z.literal(false),
})
    .strict();
export const cognitoCustomerOperationsTelemetryPolicySchema = z
    .object({
    postAuthentication: cognitoTriggerTelemetrySchema,
    postConfirmation: cognitoTriggerTelemetrySchema,
    applicationProfileCreation: z
        .object({
        authoritativeBoundary: z.literal("ensureUserIdentity"),
        authority: z.literal("SERVER"),
        atomicWithProfileCreation: z.literal(true),
    })
        .strict(),
})
    .strict();
export const cognitoCustomerOperationsTelemetryPolicy = cognitoCustomerOperationsTelemetryPolicySchema.parse({
    postAuthentication: {
        failureMode: "FAIL_OPEN",
        evidenceClass: "BEST_EFFORT_TELEMETRY",
        atomicAuthenticationTruth: false,
    },
    postConfirmation: {
        failureMode: "FAIL_OPEN",
        evidenceClass: "BEST_EFFORT_TELEMETRY",
        atomicAuthenticationTruth: false,
    },
    applicationProfileCreation: {
        authoritativeBoundary: "ensureUserIdentity",
        authority: "SERVER",
        atomicWithProfileCreation: true,
    },
});
//# sourceMappingURL=customer-operations-policy.js.map